// can() — the single authorisation function the whole codebase uses.
//
//   const verdict = can(user, 'project.update', { projectOwnerId, projectId });
//   if (!verdict.ok) throw { status: 403, code: `V2.${verdict.reason}` };
//
// `verdict.scope` tells you *why* the user was allowed — e.g. 'platform_admin'
// vs 'project_owner' vs 'invited_user'. Use this to drive audit logs and
// conditional UI ("acting as Owner" vs "acting as Admin (impersonating)").

import { CAPABILITIES } from './capabilities.js';

const UNKNOWN_CAPABILITY = (id) => ({
  ok: false,
  reason: 'UNKNOWN_CAPABILITY',
  capability: id
});

const NO_USER = { ok: false, reason: 'UNAUTHENTICATED' };

export const can = (user, capabilityId, ctx = {}) => {
  const cap = CAPABILITIES[capabilityId];
  if (!cap) return UNKNOWN_CAPABILITY(capabilityId);

  // Even capabilities that allow guests (e.g. 'project.create') still
  // need *some* user context — the orchestrator must have minted at
  // least a guest token. The exception is 'catalog.read.public' which
  // is wholly anonymous; its check function returns ok() unconditionally.
  if (!user && capabilityId !== 'catalog.read.public') {
    return NO_USER;
  }

  const verdict = cap.check(user, ctx) || NO_USER;
  // Normalise the verdict so callers don't have to defensively check.
  if (verdict.ok) {
    return { ok: true, scope: verdict.scope || 'default', capability: capabilityId };
  }
  return {
    ok: false,
    reason: verdict.reason || 'FORBIDDEN',
    capability: capabilityId
  };
};

// Batch-check helper: returns { capId: verdict } for many capabilities
// at once. Useful for the frontend useCapabilities() hook so a page
// renders the right buttons in one round-trip.
export const canMany = (user, capabilityIds, ctx = {}) => {
  const out = {};
  for (const id of capabilityIds) out[id] = can(user, id, ctx);
  return out;
};

// Quick predicate when you only want yes/no.
export const allowed = (user, capabilityId, ctx = {}) =>
  can(user, capabilityId, ctx).ok;
